// src/utils/spaceFitVerdict.js
//
// OFFICES-SPACE-FIT-METRIC: Two-lens Space Fit verdict.
//
// Lens 1 — Desk fit: people ÷ seats (how full are the desks?)
// Lens 2 — Build density: SF ÷ seats (how cramped is the building?)
//
// Four plain-language verdicts:
//   Over-Capacity — people exceed installed seats (>100% desk fill)
//   Dense         — SF/seat is below the tight threshold (cramped building)
//   Under-Used    — desks less than 80% occupied (but not cramped)
//   Right-Sized   — balanced desk fill (80–100%) and not density-constrained
//
// Escalation order: Over-Capacity → Dense → Under-Used → Right-Sized.
// Over-Capacity wins over density concerns; density wins over under-use.
//
// SF is NOT required for desk-based verdicts — when SF is absent but
// seats+people are present, Over-Capacity and Under-Used still resolve.
// Dense requires SF data. Right-Sized requires either balanced desks or
// a non-cramped density reading.
//
// This is entirely client-side — no DB reads, no server changes.
// The archived classifySpaceFit / space_fit_knobs / benchmark_bands /
// location_score* code must NOT be touched.

import { T } from "../app/tokens.js";
import {
  COMFORT_TIGHT_MIN_SF_PER_SEAT,
  COMFORT_ROOMY_MIN_SF_PER_SEAT,
} from "./comfortDensity.js";

// ── Desk-fill thresholds ──────────────────────────────────────────────────────

export const DESK_OVER_THRESHOLD    = 1.0;  // > 100% → Over-Capacity
export const DESK_BALANCED_MIN      = 0.80; // 80–100% → Balanced (Right-Sized)
// < 80% → Under-Used

// ── Verdict strings (exported for callers that need exact matching) ───────────

export const SPACE_FIT_VERDICT_OVER  = "Over-Capacity";
export const SPACE_FIT_VERDICT_DENSE = "Dense";
export const SPACE_FIT_VERDICT_UNDER = "Under-Used";
export const SPACE_FIT_VERDICT_RIGHT = "Right-Sized";

// ── Short/full display labels ─────────────────────────────────────────────────
// Single source of truth for both spellings of each verdict tier.
// Use .short in tight-column pills; use .full everywhere else (drawer, report,
// map legend, scatter legend). For Dense / Under-Used / Right-Sized the two
// spellings are identical — only Over-Capacity needs an abbreviation.

export const SPACE_FIT_LABEL = {
  [SPACE_FIT_VERDICT_OVER]:  { full: "Over-Capacity", short: "Over-Cap"      },
  [SPACE_FIT_VERDICT_DENSE]: { full: "Dense",         short: "Dense"         },
  [SPACE_FIT_VERDICT_UNDER]: { full: "Under-Used",    short: "Under-Used"    },
  [SPACE_FIT_VERDICT_RIGHT]: { full: "Right-Sized",   short: "Right-Sized"   },
};

// ── Severity order (lower = more severe; blanks always last in sort) ──────────

export const SPACE_FIT_SEVERITY = {
  [SPACE_FIT_VERDICT_OVER]:  0,
  [SPACE_FIT_VERDICT_DENSE]: 1,
  [SPACE_FIT_VERDICT_UNDER]: 2,
  [SPACE_FIT_VERDICT_RIGHT]: 3,
};

// ── Color map — V2 CSS variable tokens ───────────────────────────────────────
// Key: space_fit_verdict (internal). Used by list pills, tile pills, map markers,
// map sidebar dots, and cluster icons.

export const SPACE_FIT_COLOR = {
  // GLOBAL-PILL-STANDARDIZE: canonical positive green = T.ragGreenText (#1a6f2e),
  // matching Market Plan "Maintain" pill. Was var(--v2-green) (#1f9d6b).
  [SPACE_FIT_VERDICT_RIGHT]: T.ragGreenText,
  [SPACE_FIT_VERDICT_DENSE]: T.ragAmberText,
  [SPACE_FIT_VERDICT_OVER]:  "var(--v2-red)",
  [SPACE_FIT_VERDICT_UNDER]: T.ragAmberText,
  null:                       "var(--v2-light)",
};

export const SPACE_FIT_BG = {
  // GLOBAL-PILL-STANDARDIZE: canonical positive green bg = T.ragGreenBg (#e7f3ec),
  // matching Market Plan "Maintain" pill. Was var(--v2-green-tint) (#e9f6f0).
  [SPACE_FIT_VERDICT_RIGHT]: T.ragGreenBg,
  [SPACE_FIT_VERDICT_DENSE]: T.ragAmberBg,
  [SPACE_FIT_VERDICT_OVER]:  "var(--v2-red-tint)",
  [SPACE_FIT_VERDICT_UNDER]: T.ragAmberBg,
  null:                       "var(--v2-hair)",
};

export const SPACE_FIT_BORDER = {
  [SPACE_FIT_VERDICT_RIGHT]: "var(--v2-green-border)",
  [SPACE_FIT_VERDICT_DENSE]: T.ragAmberBorder,
  [SPACE_FIT_VERDICT_OVER]:  "var(--v2-red-border)",
  [SPACE_FIT_VERDICT_UNDER]: T.ragAmberBorder,
  null:                       "var(--v2-line)",
};

// Hex literals for environments where CSS variables cannot resolve
// (e.g. Leaflet divIcon innerHTML strings). Uses T tokens so all values
// stay in sync with src/app/tokens.js.
export const SPACE_FIT_HEX = {
  [SPACE_FIT_VERDICT_RIGHT]: T.greenDeep,
  [SPACE_FIT_VERDICT_DENSE]: T.ragAmber,
  [SPACE_FIT_VERDICT_OVER]:  T.ragRed,
  [SPACE_FIT_VERDICT_UNDER]: T.ragAmber,
  null:                       T.light,
};

// ── Shared people-vs-seats delta helper ──────────────────────────────────────

/**
 * Compute the raw people-vs-seats delta for a location.
 *
 * Returns `headcount − seats` (positive = deficit, negative/zero = surplus).
 * Returns null when either input is missing — callers must treat null as
 * "no data" and exclude the location from people-based bucketing.
 *
 * This is the single source of truth shared by the Space Fit report's
 * "Desk-fit" column and the Pulse "Space pipeline" bucketing logic so both
 * surfaces produce identical figures.
 *
 * @param {number|null} seats    Installed seats (loc.installedCapacity).
 * @param {number|null} headcount Latest headcount total. May be null.
 * @returns {number|null}  people − seats, or null when data is absent.
 */
export function computePeopleDelta(seats, headcount) {
  if (seats == null || headcount == null) return null;
  const s = Number(seats);
  const h = Number(headcount);
  if (!Number.isFinite(s) || !Number.isFinite(h)) return null;
  return h - s;
}

// ── Core computation ──────────────────────────────────────────────────────────

/**
 * Compute the Space Fit verdict for a location.
 *
 * SF is NOT required for desk-based verdicts (Over capacity / Under-used).
 * Dense requires SF data. Right-sized requires balanced desks or a non-cramped
 * density reading. Verdict is null only when seats is missing or when there
 * is no desk data AND no density data.
 *
 * @param {number|null} derivedSf  Derived rentable SF from getDisplayedSqft (never location.sqft directly).
 * @param {number|null} seats      Installed seats (loc.installedCapacity). Missing → verdict null.
 * @param {number|null} people     Latest headcount total (loc.latestHeadcount?.totalCount). May be null.
 * @returns {{
 *   verdict: string|null,   // SPACE_FIT_VERDICT_* or null
 *   densityBand: string|null, // "roomy"|"comfortable"|"cramped"|null (null when sf missing)
 *   deskBand: string|null,    // "over"|"balanced"|"under"|null (null when people missing)
 *   deskDelta: number|null,   // people - seats (null when people missing)
 * }}
 */
export function computeSpaceFit(derivedSf, seats, people) {
  const seatCount = seats != null ? Number(seats) : null;

  // Guard: seats required for any verdict
  if (seatCount == null || !Number.isFinite(seatCount) || seatCount <= 0) {
    return { verdict: null, densityBand: null, deskBand: null, deskDelta: null };
  }

  const sf = derivedSf != null ? Number(derivedSf) : null;
  const peopleCount = people != null ? Number(people) : null;

  // Desk band (fill ratio) — does NOT require SF
  let deskBand = null;
  let deskDelta = null;
  if (peopleCount != null && Number.isFinite(peopleCount)) {
    deskDelta = peopleCount - seatCount;
    const fill = peopleCount / seatCount;
    if (fill > DESK_OVER_THRESHOLD) deskBand = "over";
    else if (fill >= DESK_BALANCED_MIN) deskBand = "balanced";
    else deskBand = "under";
  }

  // Density band (SF per seat) — only when SF is present and positive
  let densityBand = null;
  if (sf != null && Number.isFinite(sf) && sf > 0) {
    const ratio = sf / seatCount;
    if (ratio >= COMFORT_ROOMY_MIN_SF_PER_SEAT) densityBand = "roomy";
    else if (ratio >= COMFORT_TIGHT_MIN_SF_PER_SEAT) densityBand = "comfortable";
    else densityBand = "cramped";
  }

  // Verdict escalation: Over capacity → Dense → Under-used → Right-sized
  let verdict;
  if (deskBand === "over") {
    // Desk demand exceeds all seats — wins regardless of density
    verdict = SPACE_FIT_VERDICT_OVER;
  } else if (densityBand === "cramped") {
    // Build is physically cramped — wins over under-use signal
    verdict = SPACE_FIT_VERDICT_DENSE;
  } else if (deskBand === "under") {
    // Desks are undersubscribed (and build is not cramped)
    verdict = SPACE_FIT_VERDICT_UNDER;
  } else if (deskBand === "balanced" || (deskBand == null && densityBand != null && densityBand !== "cramped")) {
    // Balanced desks; or no people data but density is fine
    verdict = SPACE_FIT_VERDICT_RIGHT;
  } else {
    // No usable desk or density data
    verdict = null;
  }

  return { verdict, densityBand, deskBand, deskDelta };
}
